import axios from 'axios';
import { createContext, useContext } from 'react';

/**
 * Which API the purchase panel talks to.
 *
 * The public quote page uses the quote's own public endpoints (the default,
 * unchanged). The chat details form (`ChannelFormPage`) points it at
 * `/api/channels/form/<signed token>`, adds the Telegram Mini App `initData`
 * header, and opens the insurer's payment page outside the Mini App.
 */
export interface PurchaseApi {
  /** Base URL of this quote's endpoints, no trailing slash. */
  base: (token: string) => string;
  /** Extra request headers (the Mini App's signed initData). */
  headers: Record<string, string>;
  /** Leave for the insurer's payment page. Absent: navigate this window. */
  openPayment?: (url: string) => void;
}

export const PUBLIC_QUOTES_API = '/policy/api/public/quotes';

export const defaultPurchaseApi: PurchaseApi = {
  base: (token) => `${PUBLIC_QUOTES_API}/${token}`,
  headers: {},
};

export const PurchaseApiContext = createContext<PurchaseApi>(defaultPurchaseApi);

export function usePurchaseApi(): PurchaseApi {
  return useContext(PurchaseApiContext);
}

function hasHeaders(api: PurchaseApi): boolean {
  return Object.keys(api.headers).length > 0;
}

/** No config object when there are no headers, so the public page's calls keep their exact shape. */
export function apiGet(api: PurchaseApi, url: string) {
  return hasHeaders(api) ? axios.get(url, { headers: api.headers }) : axios.get(url);
}

export function apiPost(api: PurchaseApi, url: string, body: unknown) {
  return hasHeaders(api) ? axios.post(url, body, { headers: api.headers }) : axios.post(url, body);
}
